import { useFormik } from "formik";
import React, { useState } from "react";
import { SalesForm } from "../components/contact-form/sales-form";
import * as yup from "yup";
import { validationMessages } from "../types/validation-messages";
import { postSalesFormData } from "../services/api";
import {
  backendLanguageValues,
  DISTRIBUTIONS_REGIONS,
  DISTRIBUTIONS_REGIONS_GERMAN,
  SALUTATIONS,
  SALUTATIONS_GERMAN,
} from "../components/contact-form/fields/dropdowns/values";
import { showErrorToast } from "../services/toast";
import LocalizedStrings from "react-localization";

interface SalesFormContainerProps {
  selectedLanguage: string;
}

export const SalesFormContainer: React.FC<SalesFormContainerProps> = ({
  selectedLanguage,
}) => {
  const [distributionRegionOpen, setDistributionRegionOpen] = useState(false);
  const [salutationOpen, setSalutationOpen] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Convert distribution region to the right parameter which the enum in backend endpoint accepts
  const convertDistributionRegion = (value: string) => {
    // Check if in backendLanguageValue enum or not (only Others is not to be translated)
    return backendLanguageValues.hasOwnProperty(value)
      ? (backendLanguageValues as any)[value]
      : value;
  };

  const validation = yup.object().shape({
    company: yup
      .string()
      .required(validationMessages.FIELD_REQUIRED)
      .max(50, validationMessages.TOO_MANY_CHARACTERS_INPUT),
    salutation: yup.string().required(validationMessages.SELECTION_REQUIRED),
    name: yup
      .string()
      .required(validationMessages.FIELD_REQUIRED)
      .max(50, validationMessages.TOO_MANY_CHARACTERS_INPUT),
    firstname: yup
      .string()
      .required(validationMessages.FIELD_REQUIRED)
      .max(50, validationMessages.TOO_MANY_CHARACTERS_INPUT),
    email: yup
      .string()
      .required(validationMessages.FIELD_REQUIRED)
      .matches(
        /^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/g,
        validationMessages.EMAIL_NOT_VALID
      )
      .max(50, validationMessages.TOO_MANY_CHARACTERS_INPUT),
    phoneNumber: yup
      .string()
      .max(50, validationMessages.TOO_MANY_CHARACTERS_INPUT),
    distributionRegion: yup
      .string()
      .required(validationMessages.SELECTION_REQUIRED),
    message: yup
      .string()
      .required(validationMessages.FIELD_REQUIRED)
      .max(1000, validationMessages.TOO_MANY_CHARACTERS_TEXTAREA),
    declarationOfConsent: yup
      .boolean()
      .test(
        "declaration-of-consent",
        validationMessages.DECLARATION_REQUIRED,
        (value) => value
      ),
  });

  const translations = new LocalizedStrings({
    en: {
      heading: "Contact form",
      contactPerson: "Contact person",
      contact: "Contact details",
      message: "Your Message",
      formFields: {
        company: "Company",
        salutation: "Title",
        firstName: "First Name",
        phoneNumber: "Phone",
        message: "Message",
        salutationValues: SALUTATIONS,
        regionValues: DISTRIBUTIONS_REGIONS,
        dropdownSelect: "Choose",
        dropdownRemove: "Remove selection",
      },
      declarationHeading: "Declaration of consent to data protection",
      declarationText1: "I have taken note of the ",
      declarationText2: "privacy policy",
      declarationText3: ` and give my consent to the collection and use of my data entered above.`,
      requiredField: "The fields marked with * are mandatory.",
      submit: "Submit",
      selectedLanugage: "EN",
      successUrl: "/en/contact/success",
    },
    de: {
      heading: "Kontaktformular",
      contactPerson: "Ansprechpartner",
      contact: "Kontakt",
      message: "Ihre Nachricht",
      formFields: {
        company: "Firma",
        salutation: "Anrede",
        firstName: "Vorname",
        phoneNumber: "Telefonnummer",
        message: "Nachricht",
        salutationValues: SALUTATIONS_GERMAN,
        regionValues: DISTRIBUTIONS_REGIONS_GERMAN,
        dropdownSelect: "Auswählen",
        dropdownRemove: "Auswahl löschen",
      },
      declarationHeading: "Einwilligungserklärung Datenschutz",
      declarationText1: "Ich habe die ",
      declarationText2: "Datenschutzerklärung",
      declarationText3: ` zur
        Kenntnis genommen und erteile die Einwilligung in die Erhebung und
        Nutzung meiner vorstehend eingegebenen Daten.`,
      requiredField:
        "Die mit einem * gekennzeichneten Felder sind Pflichtfelder.",
      submit: "Absenden",
      selectedLanugage: "DE",
      successUrl: "/kontact/success",
    },
  });

  translations.setLanguage(selectedLanguage);

  const formik = useFormik({
    initialValues: {
      company: "",
      salutation: "",
      name: "",
      firstname: "",
      email: "",
      phoneNumber: "",
      distributionRegion: "DE",
      message: "",
      declarationOfConsent: false,
      captchaToken: "",
      selectedLanguage: translations.selectedLanugage,
    },
    validationSchema: validation,
    onSubmit: (values) => {
      setFormSubmitting(true);
      postSalesFormData({
        ...values,
        distributionRegion: convertDistributionRegion(
          values.distributionRegion
        ),
      })
        .then(() => (window.location.href = translations.successUrl))
        .catch((error) => showErrorToast(error))
        .finally(() => setFormSubmitting(false));
    },
  });

  return (
    <SalesForm
      distributionRegionOpen={distributionRegionOpen}
      salutationOpen={salutationOpen}
      setDistributionRegionOpen={setDistributionRegionOpen}
      setSalutationOpen={setSalutationOpen}
      onSubmit={formik.handleSubmit}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      values={formik.values}
      touched={formik.touched}
      errors={formik.errors}
      setFieldValue={formik.setFieldValue}
      setFieldTouched={formik.setFieldTouched}
      formSubmitting={formSubmitting}
      translations={translations}
    />
  );
};
